import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  Bell,
  File,
  FileText,
  GitBranch,
  Hammer,
  Lock,
  MonitorSmartphone,
  Puzzle,
  StickyNote,
  Timer,
  Workflow,
  Wrench,
  BookOpen,
  Bot,
  Building2,
  Cable,
  Gauge,
  History,
  Inbox,
  FileStack,
  Globe,
  KeyRound,
  Library,
  Link2,
  Sparkles,
  Tags,
  Users,
  Layers,
  ListChecks,
  Package,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  Receipt,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Wallet,
  Webhook,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import UserMenu from "@/components/nav/UserMenu";
import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

// The platform admin area (replaces Django admin): its own shell at /admin,
// separate from the tenant dashboard. Superusers only.

type Section = { path: string; label: string; icon: LucideIcon };

const GROUPS: { label: string; items: Section[] }[] = [
  {
    label: "People",
    items: [
      { path: "/admin/tenants", label: "Tenants", icon: Building2 },
      { path: "/admin/users", label: "Users", icon: UserCog },
    ],
  },
  {
    label: "Billing",
    items: [
      { path: "/admin/plans", label: "Plans", icon: Layers },
      { path: "/admin/plan-features", label: "Plan features", icon: ListChecks },
      { path: "/admin/credit-transactions", label: "Wallet ledger", icon: Wallet },
      { path: "/admin/credit-packs", label: "Credit packs", icon: Package },
      { path: "/admin/invoices", label: "Invoices", icon: Receipt },
      { path: "/admin/usage-records", label: "Usage records", icon: Gauge },
    ],
  },
  {
    label: "Platform",
    items: [
      { path: "/admin/settings", label: "Settings", icon: Settings },
      { path: "/admin/provider-options", label: "Provider options", icon: SlidersHorizontal },
    ],
  },
  {
    label: "Templates",
    items: [
      { path: "/admin/agent-templates", label: "Agent templates", icon: FileStack },
      { path: "/admin/assistant-templates", label: "Assistant templates", icon: BookOpen },
    ],
  },
  {
    label: "Tenant data",
    items: [
      { path: "/admin/agents", label: "Agents", icon: Bot },
      { path: "/admin/assistants", label: "Assistants", icon: Sparkles },
      { path: "/admin/customers", label: "Customers", icon: Users },
      { path: "/admin/customer-plans", label: "Customer plans", icon: Tags },
      { path: "/admin/knowledge-bases", label: "Knowledge bases", icon: Library },
      { path: "/admin/webhook-endpoints", label: "Webhook endpoints", icon: Link2 },
      { path: "/admin/domains", label: "Domains", icon: Globe },
      { path: "/admin/api-keys", label: "API keys", icon: KeyRound },
      { path: "/admin/provider-credentials", label: "Provider keys", icon: Lock },
    ],
  },
  {
    label: "Advanced",
    items: [
      { path: "/admin/integrations", label: "Integrations", icon: Puzzle },
      { path: "/admin/flows", label: "Flows", icon: Workflow },
      { path: "/admin/agent-functions", label: "Agent functions", icon: Wrench },
      { path: "/admin/assistant-tools", label: "Assistant tools", icon: Hammer },
      { path: "/admin/agent-versions", label: "Agent versions", icon: GitBranch },
      { path: "/admin/prompt-templates", label: "Prompt templates", icon: FileText },
      { path: "/admin/documents", label: "Documents", icon: File },
      { path: "/admin/webhook-subscriptions", label: "Webhook subscriptions", icon: Bell },
      { path: "/admin/minute-grants", label: "Minute grants", icon: Timer },
      { path: "/admin/call-notes", label: "Call notes", icon: StickyNote },
    ],
  },
  {
    label: "Telephony",
    items: [
      { path: "/admin/phone-numbers", label: "Phone numbers", icon: Phone },
      { path: "/admin/sip-trunks", label: "SIP trunks", icon: Cable },
      { path: "/admin/number-requests", label: "Number requests", icon: Inbox },
      { path: "/admin/outbound-calls", label: "Outbound calls", icon: PhoneOutgoing },
    ],
  },
  {
    label: "Logs",
    items: [
      { path: "/admin/calls", label: "Calls", icon: PhoneCall },
      { path: "/admin/webhook-deliveries", label: "Webhook deliveries", icon: Webhook },
      { path: "/admin/tool-runs", label: "Agent tool runs", icon: Activity },
      { path: "/admin/assistant-tool-calls", label: "Assistant tool calls", icon: Activity },
      { path: "/admin/user-sessions", label: "User sessions", icon: MonitorSmartphone },
      { path: "/admin/audit-log", label: "Audit log", icon: History },
    ],
  },
];

const ALL_SECTIONS = GROUPS.flatMap((g) => g.items);

export function SuperuserOnly() {
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser);
  if (!isSuperuser) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="flex h-svh w-full bg-background">
      <aside className="hidden h-svh w-60 shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground md:flex">
        <div className="flex h-16 items-center gap-2.5 px-4">
          <div className="flex size-8 items-center justify-center rounded-lg bg-foreground text-background">
            <ShieldCheck className="size-4" />
          </div>
          <div className="text-sm font-semibold">Admin</div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-2">
          {GROUPS.map((g, gi) => (
            <div key={g.label} className={gi > 0 ? "mt-3" : undefined}>
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {g.label}
              </div>
              <div className="flex flex-col gap-1">
                {g.items.map((s) => (
                  <AdminLink key={s.path} {...s} />
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t px-2 py-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-sidebar-accent"
          >
            <ArrowLeft className="size-4" /> Back to dashboard
          </Link>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-3 border-b px-4 md:px-6">
          {/* Small screens have no sidebar, so the sections are a menu here. */}
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm md:hidden"
            value={ALL_SECTIONS.find((s) => location.pathname.startsWith(s.path))?.path ?? ""}
            onChange={(e) => navigate(e.target.value)}
          >
            {GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.items.map((s) => (
                  <option key={s.path} value={s.path}>
                    {s.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className="hidden text-sm text-muted-foreground md:block">Platform administration</div>
          <UserMenu />
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function AdminLink({ path, label, icon: Icon }: { path: string; label: string; icon: LucideIcon }) {
  return (
    <NavLink
      to={path}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}
